import React, {FC} from 'react';
import {
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {useTranslation} from 'react-i18next';
import {PalowanWithAssignmentCount} from 'models/PalowanVacationAssignment';
import 'components/StickyHeadTable/StickyHeadTable.scss';

export interface PalowanGroupedTableProps {
  /** Palowan rows with aggregated range counts to display. */
  data: PalowanWithAssignmentCount[];
  /** When true, show skeleton rows instead of real data. */
  loading: boolean;
  /** Current 1-based page number. */
  page: number;
  /** Number of records per page. */
  pageSize: number;
  /** Total number of matching palowans across all pages. */
  totalCount: number;
  /** Called with the new 1-based page number when the user navigates. */
  onPageChange: (page: number) => void;
  /** Called with the full palowan object when the edit action is triggered. */
  onEdit: (palowan: PalowanWithAssignmentCount) => void;
}

/** Number of skeleton rows to render while loading. */
const SKELETON_ROW_COUNT = 5;

/** Column header definitions for the grouped palowan table. */
const COLUMNS = [
  {
    id: 'palowan',
    labelKey: 'leaves.PalowanGroupedTable.columns.palowan',
    align: 'left' as const,
  },
  {
    id: 'ranges',
    labelKey: 'leaves.PalowanGroupedTable.columns.ranges',
    align: 'left' as const,
  },
  {
    id: 'actions',
    labelKey: 'leaves.PalowanGroupedTable.columns.actions',
    align: 'center' as const,
  },
];

/**
 * Table that displays one row per palowan with their total vacation range count.
 * The edit button opens the PalowanRangesModal for inline range management.
 *
 * @param data - Palowan rows to render.
 * @param loading - Renders skeleton rows when true.
 * @param page - Current pagination page (1-based).
 * @param pageSize - Records per page.
 * @param totalCount - Total matching records used by the pagination control.
 * @param onPageChange - Pagination handler (receives 1-based page).
 * @param onEdit - Called when the edit button is clicked; opens the ranges modal.
 * @returns A MUI Table with pagination.
 */
const PalowanGroupedTable: FC<PalowanGroupedTableProps> = ({
  data,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onEdit,
}) => {
  const {t} = useTranslation();

  const handlePageChange = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    // MUI TablePagination uses 0-based pages; the API uses 1-based pages
    onPageChange(newPage + 1);
  };

  // Clamp the MUI 0-based page to prevent out-of-range warnings during page transitions
  const maxMuiPage = totalCount > 0 ? Math.ceil(totalCount / pageSize) - 1 : 0;
  const safeMuiPage = Math.min(page - 1, maxMuiPage);

  const renderSkeletonRows = () =>
    Array.from({length: SKELETON_ROW_COUNT}).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        {COLUMNS.map(col => (
          <TableCell key={col.id}>
            <Skeleton variant="text" width="80%" />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={COLUMNS.length} align="center">
        <Typography variant="body2" color="text.secondary" className="py-4">
          {t('leaves.PalowanGroupedTable.emptyState')}
        </Typography>
      </TableCell>
    </TableRow>
  );

  const renderDataRows = () =>
    data.map(palowan => (
      <TableRow key={palowan.palowanId} hover>
        <TableCell style={{wordBreak: 'break-word'}}>
          {`${palowan.firstName} ${palowan.lastName}`}
        </TableCell>
        <TableCell style={{wordBreak: 'break-word'}}>
          {t('leaves.PalowanGroupedTable.rangesCount', {
            count: palowan.totalRangeCount,
          })}
        </TableCell>
        <TableCell align="center">
          <IconButton
            size="medium"
            aria-label="Edit palowan ranges"
            onClick={() => onEdit(palowan)}
          >
            <EditIcon fontSize="medium" />
          </IconButton>
        </TableCell>
      </TableRow>
    ));

  return (
    <Paper className="StickyHeadTable" sx={{width: '100%', overflow: 'hidden'}}>
      <TableContainer>
        <Table
          stickyHeader
          aria-label="Palowan vacation assignment grouped table"
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell key={col.id} align={col.align}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t(col.labelKey)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? renderSkeletonRows()
              : data.length === 0
              ? renderEmptyState()
              : renderDataRows()}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={safeMuiPage}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[pageSize]}
      />
    </Paper>
  );
};

export default PalowanGroupedTable;
